import { Injectable } from '@angular/core';
import { BookmarkService, createBookmarkService } from 'fl-bookmarks';
import {
  ContentAuthorizationConfiguration,
  ContentAuthorizer,
  createContentAuthorizer,
} from 'fl-content-authorization';
import { FavouriteService, createFavouriteService } from 'fl-favourites';
import { Error, isError } from 'fl-foundation';
import {
  AuthorizationData,
  PlatformAuthorizer,
  PlatformClient,
  PlatformConfig,
  UserAuthorizationData,
  UserAuthorizationDelegate,
  createPlatformAuthorizer,
  createPlatformClient,
} from 'fl-platform-authorizer';
import { applicationConstants } from '../../applicationConstants';
import { KEYS } from '../../utils/constants/storage.constants';
import { getDeviceType } from '../../utils/deviceUtils';
import {
  expTimeHasExpired,
  setExpirationTime,
  decodeToken,
  saveTokenExpiry,
} from '../../utils/LoginUtils';
import { ConfigService } from '../config.service';
import { DeviceHandlerService } from '../device-handler.service';
import { EntitlementService } from '../evergent/entitlement/entitlement.service';
import { GetEntitlementsResponseMessage } from '../evergent/entitlement/model/entitlement/entitlement.response.message';
import { TokenService } from '../firstlight/guest-token/token.service';
import { LocalStorageService } from '../storage/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private contentAuthConfiguration?: ContentAuthorizationConfiguration;
  private platformConfig?: PlatformConfig;
  public contentAuthorizer?: ContentAuthorizer;
  public platformAuthorizer?: PlatformAuthorizer;
  public bookmarkService?: BookmarkService;
  public favoriteService?: FavouriteService;
  private widevineCertificate!: ArrayBuffer;
  public showThumbnails = true;

  constructor(
    private readonly configService: ConfigService,
    private readonly localStorage: LocalStorageService,
    private readonly deviceHandlerService: DeviceHandlerService,
    private readonly tokenService: TokenService,
    private readonly entitleService: EntitlementService
  ) {}

  isLoggedIn(): boolean {
    return this.localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN)?.length > 0;
  }

  public async initPlatformService(): Promise<AuthorizationData | Error> {
    if (!this.platformAuthorizer) {
      this.createPlatformAuthoriser();
    }
    const authData: AuthorizationData | Error =
      await this.platformAuthorizer.ensureAuthorization();
    if (!isError(authData)) {
      this.localStorage.setItem(KEYS.PLATFORM_TOKEN, authData.accessToken);
    }
    this.initAppServices();
    await this.setupCertificate();
    return Promise.resolve(authData);
  }

  private async setupCertificate() {
    const navigatorAgent = navigator.userAgent;
    const chrome = navigatorAgent
      .substr(navigatorAgent.indexOf('Chrome/'), navigatorAgent.indexOf('.'))
      .split('/');
    if (
      parseInt(chrome[1], 10) < 40 ||
      (!navigatorAgent.includes('Web0S') && !navigatorAgent.includes('Tizen'))
    ) {
      await this.fetchWVCertificate();
    }
  }

  private initAppServices(): void {
    const config = this.configService.applicationConfig;
    this.bookmarkService = createBookmarkService(
      config.bookmarkURL,
      this.platformAuthorizer
    );
    this.contentAuthorizer = this.createContentAuthorizer();
    this.favoriteService = createFavouriteService(
      config.favoriteURL,
      this.platformAuthorizer
    );
  }

  private createPlatformAuthoriser() {
    const config = this.configService.applicationConfig;
    this.platformConfig = {
      clientID: config.clientID,
      clientSecret: config.clientSecret,
      xClientId: config.xClientId,
      authorizationEndPoint: config.oAuthURL,
    };
    this.platformAuthorizer = createPlatformAuthorizer(
      this.platformConfig,
      this.getAuthDelegate()
    );
  }

  private getAuthDelegate(): UserAuthorizationDelegate {
    const userAuthorizationDelegate: UserAuthorizationDelegate = {
      fetchUserAuthorizationToken: () => {
        return this.getAccessToken();
      },
    };
    return userAuthorizationDelegate;
  }

  async getAccessToken(): Promise<UserAuthorizationData | Error> {
    const ovatToken = this.localStorage.getItem(KEYS.EVERGENT_OVAT_TOKEN);
    if (
      ovatToken === null ||
      ovatToken === '' ||
      expTimeHasExpired(KEYS.EVERGENT_OVAT_TOKEN_EXPIRY)
    ) {
      if (!this.isLoggedIn()) {
        const authResponse: AuthorizationData | Error =
          await this.platformAuthorizer.ensureAuthorization();
        if (!isError(authResponse)) {
          const authData = authResponse;
          this.localStorage.setItem(KEYS.PLATFORM_TOKEN, authData.accessToken);
          const result = await this.tokenService.fetchOvatToken(
            this.deviceHandlerService.getCurrentDeviceID(),
            getDeviceType(),
            authData
          );

          this.localStorage.setItem(KEYS.EVERGENT_OVAT_TOKEN, result.token);
          setExpirationTime(result.expires_in, KEYS.EVERGENT_OVAT_TOKEN_EXPIRY);
          return { accessToken: result.token };
        }
        return authResponse;
      } else {
        const response = await this.entitleService
          .getEntitlement(
            this.localStorage.getItem(KEYS.SELECTED_PROFILE_ID) || ''
          )
          .toPromise();
        this.entitleService.handleEntitlementResult(response);
        return {
          accessToken: this.localStorage.getItem(KEYS.EVERGENT_OVAT_TOKEN),
        };
      }
    }
    return { accessToken: ovatToken || '' };
  }

  public async fetchWVCertificate(): Promise<ArrayBuffer> {
    if (this.widevineCertificate) {
      return this.widevineCertificate;
    }
    if (!this.configService.applicationConfig.widevineCertBinUrl) {
      this.configService.applicationConfig.widevineCertBinUrl =
        'https://storage.googleapis.com/wvmedia/cert/cert_license_widevine_com_prod.bin';
    }
    const request = await fetch(
      this.configService.applicationConfig.widevineCertBinUrl
    );
    this.widevineCertificate = await request.arrayBuffer();

    return this.widevineCertificate;
  }

  private createContentAuthorizer(): ContentAuthorizer {
    const config = this.configService.applicationConfig;
    this.contentAuthConfiguration = {
      endPointUrl: config.contentAuthURL,
      clientRegistrationEndPointUrl: config.clientRegURL,
      renewLicenseEndPointUrl: config.licenseRefreshURL,
    };
    return createContentAuthorizer(
      this.contentAuthConfiguration,
      this.platformAuthorizer,
      this.getPlatformClient()
    );
  }

  private getPlatformClient(): PlatformClient {
    return createPlatformClient(
      this.deviceHandlerService.getCurrentDeviceID(),
      getDeviceType()
    );
  }

  public resetPlayback(): void {
    if (this.platformAuthorizer) {
      this.platformAuthorizer.dispose();
      this.platformAuthorizer = null;
    }
    if (this.contentAuthorizer) {
      this.contentAuthorizer.invalidateDeviceToken();
      this.contentAuthorizer = null;
    }
  }

  deviceAudioSupport() {
    return this.getPlatformClient().supportedAudioCodecs;
  }

  isAtmos() {
    return Boolean(
      this.deviceAudioSupport()?.includes(applicationConstants.EAC3_JOC)
    );
  }

  isDolby() {
    return Boolean(
      this.deviceAudioSupport()?.includes(applicationConstants.EAC3)
    );
  }

  getAccessTokenExpiry(): number | null {
    const storedExpiry = this.localStorage.getItem(
      KEYS.EVERGENT_ACCESS_TOKEN_EXPIRY
    );

    if (storedExpiry) {
      return parseInt(storedExpiry, 10);
    }

    // Fallback for existing users (Lazy Migration)
    const token = this.localStorage.getItem(KEYS.EVERGENT_ACCESS_TOKEN);
    return saveTokenExpiry(token || '');
  }

  /**
   * Checks if the access token is nearly expired (e.g., within 30 seconds)
   * @returns boolean - True if token is nearly expired
   */
  isTokenNearlyExpired(): boolean {
    const expiry = this.getAccessTokenExpiry();
    if (!expiry) {
      return false;
    }
    const currentTime = Math.floor(Date.now() / 1000);
    // Refresh if expiring in less than 30 seconds
    return expiry - currentTime < 30;
  }
}
